import React, { useEffect, useState } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ArrowRight,
  Bot,
  Check,
  ChevronDown,
  Clock,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import {
  rowViewOf,
  styleColorOf,
  exposureOf,
  fmtMoney,
  fmtMoneyCompact,
  timeLabel,
} from './shell/useAgentTickets';
import WorklistTable from './carbon/WorklistTable';

// ============================================================================
// CHA-273 — unified Agents cockpit (design: ChainTo Ops - Concept)
// ============================================================================
// ONE work queue across both agents (Yulie past-cancel + Ella GP drift),
// replacing YuliePage/EllaPage and their per-rule lists/drawers. Purely
// presentational over the SHARED useAgentTickets view model passed down from
// InMoceanDashboard (one source of truth — no second subscription, so the
// cockpit can never disagree with the sidebar badge or Today, CHA-239).
// ALL writes go through the auth-gated `yulieTicketAction` callable with the
// exact legacy payloads — never direct Firestore writes (CHA-133 lesson).
// InMocean-only (Path 1); tenant path hardcoded like the detection runtime,
// generalization deferred to CHA-25.
const TENANT_ID = 'inmocean';

// Owner default: the "WHILE YOU WERE OUT" briefing card ships built but OFF
// (design README: `showBriefing: false`).
const SHOW_BRIEFING = false;

const RULE_LABELS = {
  past_cancel: 'Past-cancel',
  cost_sheet_gp_drift: 'Cost-sheet GP drift',
};

// Agent-tinted "read" box borders (~0.35 alpha per the design tokens).
const READ_BORDERS = {
  yulie: 'rgba(105,41,196,0.35)',
  ella: 'rgba(0,125,121,0.35)',
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtPct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');
const fmtMoneyOrDash = (n) => (typeof n === 'number' ? fmtMoney(n) : '—');

const toDateSafe = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Blue "NEW" chip: state new AND created within the last 24h.
export const isNewTicket = (t, nowMs = Date.now()) => {
  if (t?.state !== 'new') return false;
  const d = toDateSafe(t.created_at);
  return !!d && nowMs - d.getTime() <= 24 * 3600e3;
};

// Snooze presets: "To Friday" (Ops concept primary) + the legacy SnoozeMenu
// durations. Returns epoch millis, or null for a permanent snooze — the same
// `until` contract yulieTicketAction has always received.
export const snoozeUntil = (key, from = new Date()) => {
  const eod = new Date(from);
  eod.setHours(23, 59, 59, 999);
  switch (key) {
    case 'fri': {
      const d = new Date(eod);
      const delta = (5 - d.getDay() + 7) % 7 || 7; // upcoming Friday; a full week if today is Friday
      d.setDate(d.getDate() + delta);
      return d.getTime();
    }
    case 'eod':
      return eod.getTime();
    case '1d':
      return from.getTime() + 86400000;
    case '3d':
      return from.getTime() + 3 * 86400000;
    case '1wk':
      return from.getTime() + 7 * 86400000;
    case 'permanent':
    default:
      return null;
  }
};

const SNOOZE_OPTIONS = [
  ['fri', 'To Friday'],
  ['eod', 'End of day'],
  ['1d', '1 day'],
  ['3d', '3 days'],
  ['1wk', '1 week'],
  ['permanent', 'Permanent'],
];

// Row right-column sub-metric — same figures the legacy lists showed
// (Yulie: GP %; Ella: GP $). No new margin visibility (CHA-138 boundary).
const subMetricOf = (t) => {
  const ev = t?.evidence || {};
  if (t.rule === 'past_cancel') return typeof ev.gp_pct === 'number' ? `GP ${fmtPct(ev.gp_pct)}` : '';
  if (t.rule === 'cost_sheet_gp_drift')
    return typeof ev.ext_gp === 'number' ? `GP ${fmtMoneyCompact(ev.ext_gp)}` : '';
  return '';
};

// Queue columns for the canonical WorklistTable (CHA-283) — the exact cockpit
// row anatomy (64px mono metric | main | 104px exposure; the primitive appends
// the chevron column). Presentation only; same figures as before.
const QUEUE_COLUMNS = [
  {
    key: 'metric',
    width: '64px',
    render: (t) => {
      const row = rowViewOf(t);
      return (
        <div>
          <div className="font-plex-mono text-base font-semibold leading-tight" style={{ color: row.sev }}>
            {row.metric}
          </div>
          <div className="font-plex-mono text-[9.5px] tracking-[0.1em] uppercase text-carbon-gray-50">
            {row.caption}
          </div>
        </div>
      );
    },
  },
  {
    key: 'main',
    width: 'minmax(0,1fr)',
    render: (t) => {
      const row = rowViewOf(t);
      return (
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <span className="font-semibold text-sm text-carbon-ink whitespace-nowrap">
              PO {t.po_no || t.id}
            </span>
            <span className="text-xs text-carbon-gray-50 overflow-hidden text-ellipsis whitespace-nowrap">
              {styleColorOf(t)}
            </span>
            {isNewTicket(t) ? (
              <span
                className="font-plex-mono text-[9px] tracking-[0.08em] px-1.5 py-px whitespace-nowrap"
                style={{ background: 'rgba(15,98,254,0.1)', color: '#0F62FE' }}
              >
                NEW
              </span>
            ) : null}
            {t.state === 'snoozed' ? (
              <span className="font-plex-mono text-[9px] tracking-[0.08em] uppercase text-carbon-gray-40 border border-carbon-gray-20 px-1 py-px whitespace-nowrap">
                snoozed
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5 mt-[3px] text-[12.5px] text-carbon-gray-70 min-w-0">
            <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: row.agent.color }} />
            <span className="font-semibold flex-shrink-0">{row.agent.name}</span>
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">· {row.suggested}</span>
          </div>
        </div>
      );
    },
  },
  {
    key: 'exposure',
    width: '104px',
    align: 'right',
    render: (t) => {
      const sub = subMetricOf(t);
      return (
        <div className="text-right">
          <div className="font-plex-mono text-sm font-semibold text-carbon-ink">{fmtMoney(exposureOf(t))}</div>
          {sub ? <div className="font-plex-mono text-[10.5px] text-carbon-gray-50">{sub}</div> : null}
        </div>
      );
    },
  },
];

// "[Agent]'s read" — static rule-derived copy for v1 (epic constraint: no LLM
// copy), composed only from evidence fields the legacy pages already showed.
const agentReadOf = (t) => {
  const ev = t?.evidence || {};
  if (t.rule === 'past_cancel') {
    const days = num(ev.days_past_cancel);
    const urgent = days >= 14;
    return (
      `Open ${days} day${days === 1 ? '' : 's'} past the cancel date with ` +
      `${fmtMoney(exposureOf(t))} of gross exposure still on the order. ` +
      (urgent
        ? 'This is past the 14-day urgency line — a ship-or-cancel decision needs a human call before it ages further.'
        : 'Still under the 14-day urgency line — tickets at this stage often clear once the warehouse releases a pick.')
    );
  }
  if (t.rule === 'cost_sheet_gp_drift') {
    const gp = num(ev.gp_pct);
    const urgent = gp < 10;
    const worst =
      ev.worst_line_style || ev.worst_line_color
        ? ` The worst line (${ev.worst_line_style || '—'} / ${ev.worst_line_color || '—'}) sits at ${fmtPct(
            typeof ev.worst_line_gp_pct === 'number' ? ev.worst_line_gp_pct : undefined
          )} line GP.`
        : '';
    return (
      `Rolled-up GP is ${gp.toFixed(1)}% across ${ev.line_count ?? '—'} line${ev.line_count === 1 ? '' : 's'}.` +
      worst +
      (urgent
        ? ' This is deep drift — check the cost-sheet actuals against the quote before this invoices.'
        : ' Borderline drift — compare the quoted vs actual uploads before acting.')
    );
  }
  return 'Review the ticket evidence below.';
};

// Drawer 2-col stat cards. Ticket-evidence fields plus the lazy-loaded
// po_record fields the legacy drawers surfaced — nothing new.
const drawerStatsOf = (t, po, poLoading) => {
  const ev = t?.evidence || {};
  const poVal = (v) => (poLoading ? '…' : v || '—');
  if (t.rule === 'past_cancel') {
    const days = num(ev.days_past_cancel);
    return [
      { label: 'Days past cancel', value: String(days), accent: true },
      { label: 'Gross exposure', value: fmtMoneyOrDash(ev.gross_ext_price) },
      { label: 'GP %', value: fmtPct(ev.gp_pct) },
      { label: 'Cancel date', value: poVal(po?.cancel_date) },
      { label: 'Pick #', value: poVal(po?.current_pick_number) },
      { label: 'Status', value: poVal(po?.current_state) },
    ];
  }
  if (t.rule === 'cost_sheet_gp_drift') {
    return [
      { label: 'Rolled-up GP %', value: fmtPct(ev.gp_pct), accent: typeof ev.gp_pct === 'number' && ev.gp_pct < 10 },
      { label: 'Gross exposure', value: fmtMoneyOrDash(ev.gross_ext_price) },
      { label: 'GP $', value: fmtMoneyOrDash(ev.ext_gp) },
      { label: 'Lines', value: String(ev.line_count ?? '—') },
      { label: 'Worst line GP %', value: fmtPct(ev.worst_line_gp_pct) },
      { label: 'Worst line gross', value: fmtMoneyOrDash(ev.worst_line_gross) },
    ];
  }
  return [{ label: 'Gross exposure', value: fmtMoneyOrDash(ev.gross_ext_price) }];
};

// ============================================================================
// Main cockpit
// ============================================================================
export default function AgentsCockpit({ agentTickets, initialFilter = 'all' }) {
  const {
    open = [],
    ranked = [],
    openCount = 0,
    urgentCount = 0,
    openExposure = 0,
    clearedCount = 0,
    clearedExposure = 0,
    avgClearDays = null,
    agents = [],
    feed = [],
    loading = false,
    error = null,
  } = agentTickets || {};

  const [filter, setFilterState] = useState(initialFilter);
  const [selectedId, setSelectedId] = useState(null);
  const [acting, setActing] = useState(false);

  // Route changes (#/agents/yulie ⇄ #/agents/ella ⇄ #/agents) re-target the
  // filter without remounting (drawer state survives irrelevant hash churn).
  useEffect(() => {
    setFilterState(initialFilter);
  }, [initialFilter]);

  // Chip clicks write the filter BACK to the hash so reload / back-nav /
  // copied links agree with what's on screen (the legacy per-agent tabs
  // updated the hash too). The hashchange listener in AgentsHome then feeds
  // it back down as initialFilter — one source of truth, the URL.
  const setFilter = (key) => {
    setFilterState(key);
    window.location.hash = key === 'all' ? '#/agents' : `#/agents/${key}`;
  };

  // Selected ticket resolves live against the model: when a snapshot removes
  // it (snoozed away by the callable, closed, auto-resolved) the drawer closes
  // itself — the shared model is never hand-mutated.
  const selected = open.find((t) => t.id === selectedId) || null;

  const callAction = async (payload) => {
    setActing(true);
    try {
      const fn = httpsCallable(getFunctions(), 'yulieTicketAction');
      // CHA-591 — yulieTicketAction resolves tenant server-side, but a platform admin (tenantId=null)
      // must NAME the tenant (resolveTenant does not guess). This file is src/tenants/inmocean/, so it
      // names its own tenant via the existing TENANT_ID constant. A tenant-scoped user's value is
      // ignored server-side, so this is safe for everyone. Do NOT copy this literal into platform code.
      await fn({ ...payload, tenantId: TENANT_ID });
      // The Firestore subscription updates the queue; just close the drawer.
      setSelectedId(null);
    } catch (err) {
      console.error('yulieTicketAction failed', err);
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Action failed. Please try again.');
    } finally {
      setActing(false);
    }
  };

  const handleSnooze = (untilMs) =>
    selected && callAction({ action: 'snooze', ticketId: selected.id, until: untilMs });
  const handleMarkOk = (reason) =>
    selected && callAction({ action: 'mark_ok', ticketId: selected.id, reason });

  const kpis = [
    { label: 'At-risk exposure', value: fmtMoneyCompact(openExposure), note: 'open queue', color: '#161616' },
    {
      label: 'Open exceptions',
      value: String(openCount),
      note: urgentCount > 0 ? `${urgentCount} urgent` : 'none urgent',
      color: '#161616',
    },
    {
      label: 'Cleared this week',
      value: String(clearedCount),
      note: clearedCount > 0 ? `${fmtMoneyCompact(clearedExposure)} resolved` : 'past 7 days',
      color: '#24A148',
    },
    {
      label: 'Time-to-clear',
      value: avgClearDays == null ? '—' : `${avgClearDays.toFixed(1)}d`,
      note: 'avg, cleared this week',
      color: '#161616',
    },
  ];

  const emptyDetail =
    filter === 'yulie'
      ? 'No open past-cancel tickets.'
      : filter === 'ella'
        ? 'No open GP-drift tickets.'
        : 'Yulie and Ella are watching. New exceptions will land here.';

  return (
    <div className="font-plex text-carbon-ink">
      {/* Breadcrumb */}
      <div className="flex items-baseline gap-2.5 mb-6">
        <span className="font-plex-mono text-[11px] tracking-[0.12em] uppercase text-carbon-gray-50">
          Intelligence
        </span>
        <span className="text-carbon-gray-30">/</span>
        <h1 className="text-[26px] font-semibold tracking-[-0.01em] m-0">Agents</h1>
      </div>

      {SHOW_BRIEFING ? <BriefingCard open={open} openCount={openCount} openExposure={openExposure} agents={agents} /> : null}

      {/* KPI strip — 4 white cards (Ops concept) */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3.5 mb-5">
        {kpis.map((k) => (
          <div key={k.label} className="bg-carbon-white border border-carbon-border px-[18px] py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-2">
              {k.label}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-plex-mono text-2xl font-semibold" style={{ color: k.color }}>
                {loading ? '…' : k.value}
              </span>
              <span className="text-[11.5px] text-carbon-gray-50">{k.note}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Queue + right rail */}
      <div className="flex flex-wrap gap-5 items-start">
        {/* Work queue — the canonical WorklistTable (CHA-283). Same rows,
            filters, states and callable-gated drawer actions; the primitive
            only owns the table/drawer chrome. */}
        <div className="basis-[480px] grow-[999] shrink min-w-0">
          <WorklistTable
            columns={QUEUE_COLUMNS}
            rows={ranked}
            rowKey={(t) => t.id}
            rowTestId="queue-row"
            filterBar={{
              title: 'Work queue',
              chips: [
                { key: 'all', label: 'All' },
                { key: 'yulie', label: 'Yulie' },
                { key: 'ella', label: 'Ella' },
              ],
              chipOf: (t) => rowViewOf(t).agent.key,
              // Controlled by the hash-synced filter (one source of truth, the URL).
              activeChip: filter,
              onChipChange: setFilter,
              // A zero agent slice is good news, not "no results".
              emptyAsGoodNews: true,
            }}
            severityOf={(t) => rowViewOf(t).sev}
            onRowClick={(t) => setSelectedId(t.id)}
            selectedKey={selectedId}
            loading={loading}
            error={
              error
                ? {
                    what: 'Live agent data is unavailable.',
                    staleNote: error,
                    // Never fabricate "queue clear" when the subscription
                    // failed — empty on error is unknown (TodayHome pattern).
                    emptyText: 'Queue unavailable — see the notice above.',
                  }
                : null
            }
            empty={{ detail: emptyDetail }}
            drawer={{
              width: 480,
              ariaLabelOf: (t) => `Ticket PO ${t.po_no || t.id}`,
              onClose: () => setSelectedId(null),
              closeDisabled: acting,
              render: (t) => (
                <CockpitDrawer
                  ticket={t}
                  busy={acting}
                  onClose={() => setSelectedId(null)}
                  onSnooze={handleSnooze}
                  onMarkOk={handleMarkOk}
                />
              ),
            }}
          />
        </div>

        {/* Right rail */}
        <div className="basis-[280px] grow shrink flex flex-col gap-4">
          {/* Agent roster */}
          <div className="bg-carbon-white border border-carbon-border px-[18px] py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.14em] uppercase text-carbon-gray-50 mb-3">
              Your agents
            </div>
            <div className="flex flex-col gap-3">
              {agents.map((a) => (
                <div key={a.rule} className="flex gap-2.5 items-center">
                  <div
                    className="w-[34px] h-[34px] flex items-center justify-center flex-shrink-0"
                    style={{ background: a.tint, color: a.color }}
                  >
                    <Bot size={17} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold">
                      {a.name} <span className="font-normal text-carbon-gray-50">· {a.sub}</span>
                    </div>
                    <div className="font-plex-mono text-[10.5px] text-carbon-gray-50">
                      {(a.lastRun ? `ran ${timeLabel(a.lastRun)}` : 'idle') +
                        ` · ${a.openCount} open` +
                        (a.newToday > 0 ? ` · ${a.newToday} new` : '')}
                    </div>
                  </div>
                  <span className="w-[7px] h-[7px] rounded-full bg-carbon-green-50 animate-pulse flex-shrink-0" />
                </div>
              ))}
              {/* Next-skill placeholder slot (Ops concept) */}
              <div className="flex gap-2.5 items-center border border-dashed border-carbon-gray-30 px-2.5 py-[9px]">
                <div className="w-[30px] h-[30px] flex items-center justify-center bg-carbon-gray-10 text-carbon-gray-50 flex-shrink-0">
                  <Plus size={15} />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-carbon-gray-70">
                    Daniel <span className="font-normal text-carbon-gray-50">· invoice anomalies</span>
                  </div>
                  <div className="font-plex-mono text-[10px] text-carbon-gray-30">next skill — queued</div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div className="bg-carbon-white border border-carbon-border px-[18px] py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.14em] uppercase text-carbon-gray-50 mb-2.5">
              Activity
            </div>
            {feed.length === 0 ? (
              <div className="text-xs text-carbon-gray-50 py-1.5">No recent activity.</div>
            ) : (
              feed.map((f, i) => (
                <div key={i} className="flex gap-2.5 py-[7px] border-t border-carbon-gray-10">
                  <span className="font-plex-mono text-[10px] text-carbon-gray-30 w-[52px] flex-shrink-0 pt-0.5">
                    {f.time}
                  </span>
                  <div className="text-[12.5px] leading-relaxed text-carbon-gray-70">
                    <span className="font-semibold" style={{ color: f.whoColor }}>
                      {f.who}
                    </span>{' '}
                    {f.text}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="text-[11.5px] text-carbon-gray-50 leading-relaxed px-1">
            Every decision you make here is logged — and teaches the agents what &ldquo;normal&rdquo; looks
            like.
          </div>
        </div>
      </div>

    </div>
  );
}

// ============================================================================
// Briefing card — built per the Ops concept, rendered only when SHOW_BRIEFING
// flips on. Honest numbers only (from the shared model); no fabricated scan
// stats — real per-run stats need a run doc the runtime doesn't write today
// (flagged on CHA-239).
// ============================================================================
function BriefingCard({ open, openCount, openExposure, agents }) {
  const newTickets = open.filter((t) => isNewTicket(t));
  const newExposure = newTickets.reduce((s, t) => s + exposureOf(t), 0);
  const stamp = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return (
    <div className="relative overflow-hidden bg-carbon-ink px-7 py-[26px] mb-5">
      {/* purple → teal gradient top hairline */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: 'linear-gradient(90deg, #8A3FFC, #08BDBA 40%, transparent 75%)' }}
      />
      <div className="flex justify-between items-start gap-6 flex-wrap">
        <div className="min-w-[320px] flex-1">
          <div className="font-plex-mono text-[10.5px] tracking-[0.16em] uppercase text-carbon-gray-50 mb-2.5">
            While you were out — {stamp}
          </div>
          <div className="text-[22px] font-semibold text-carbon-gray-10 tracking-[-0.01em] leading-snug max-w-[560px]">
            {newTickets.length} new exception{newTickets.length === 1 ? '' : 's'} in the last 24h —{' '}
            <span className="text-carbon-red-40">{fmtMoneyCompact(newExposure)}</span> of added exposure
            needs a human call.
          </div>
          <div className="flex gap-2.5 mt-4 flex-wrap">
            {agents.map((a) => (
              <div
                key={a.rule}
                className="flex items-center gap-2 px-3 py-2 border"
                style={{ background: a.tint, borderColor: READ_BORDERS[a.key] || a.color }}
              >
                <Bot size={14} style={{ color: a.color }} />
                <span className="text-[12.5px] text-carbon-gray-30">
                  <span className="font-semibold" style={{ color: a.color }}>
                    {a.name}
                  </span>{' '}
                  — {a.openCount} open{a.newToday > 0 ? `, ${a.newToday} new` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="font-plex-mono text-[10.5px] tracking-[0.14em] uppercase text-carbon-gray-50">
            Queue total
          </div>
          <div className="font-plex-mono text-[34px] font-semibold text-carbon-gray-10 leading-tight">
            {fmtMoneyCompact(openExposure)}
          </div>
          <div className="font-plex-mono text-[11.5px] text-carbon-gray-50">{openCount} open exceptions</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Drawer CONTENT — 480px right slide-over (Ops concept). The chrome (overlay,
// backdrop, Escape, focus restore) is owned by WorklistTable's drawer slot
// (CHA-283); this renders the header / body / footer and keeps the exact
// callable-gated actions. Lazy-loads the shared po_record exactly like the
// legacy drawers (validated po_ref shape, one-time get). Backdrop close is
// disabled while an action is in flight (closeDisabled={acting} at the slot).
// ============================================================================
function CockpitDrawer({ ticket, busy, onClose, onSnooze, onMarkOk }) {
  const [po, setPo] = useState(null);
  const [poLoading, setPoLoading] = useState(true);
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState('');
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const db = getFirestore();

  // 4th-layer review (#207): Escape must peel the INNERMOST layer first and never destroy typed
  // operator input — the primitive's document-level Escape closes the whole drawer, so absorb it
  // here (capture phase) while the snooze popover or the mark-OK reason form is open.
  useEffect(() => {
    if (!snoozeOpen && !showReason) return undefined;
    const absorb = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (snoozeOpen) setSnoozeOpen(false);
      else if (showReason) { setShowReason(false); setReason(''); }
    };
    document.addEventListener('keydown', absorb, true); // capture: runs before the drawer's handler
    return () => document.removeEventListener('keydown', absorb, true);
  }, [snoozeOpen, showReason]);

  useEffect(() => {
    let cancelled = false;
    setPoLoading(true);
    setPo(null);
    // Validate the full po_ref shape (not just the prefix) before doc(): a malformed
    // ref — e.g. an empty id — makes doc() throw synchronously and break the drawer.
    // [^/] keeps the %2F-encoded ids (CHA-132) valid while rejecting raw extra segments.
    const poRef = typeof ticket.po_ref === 'string' ? ticket.po_ref.trim() : '';
    let ref = null;
    if (/^po_records\/[^/]+$/.test(poRef)) {
      try {
        ref = doc(db, `tenants/${TENANT_ID}/${poRef}`);
      } catch (e) {
        console.error('drawer: invalid po_ref path', e);
      }
    }
    if (!ref) {
      setPoLoading(false);
      return undefined;
    }
    getDoc(ref)
      .then((snap) => {
        if (cancelled) return;
        setPo(snap.exists() ? snap.data() : null);
        setPoLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('drawer: po_record load failed', e);
        setPoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket.po_ref, db]);

  const row = rowViewOf(ticket);
  const agent = row.agent;
  const ruleLabel = RULE_LABELS[ticket.rule] || ticket.rule || 'Ticket';
  const stats = drawerStatsOf(ticket, po, poLoading);
  const validReason = reason.trim().length > 0;

  return (
    <>
      {/* Header (white) */}
        <div className="bg-carbon-white border-b border-carbon-border px-6 py-5 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-[7px] h-[7px] rounded-full" style={{ background: agent.color }} />
              <span
                className="font-plex-mono text-[10.5px] tracking-[0.14em] uppercase"
                style={{ color: agent.color }}
              >
                {ruleLabel}
              </span>
            </div>
            <h2 className="font-plex-mono text-[22px] font-semibold tracking-[-0.01em] m-0">
              PO {ticket.po_no || ticket.id}
            </h2>
            <div className="text-[13px] text-carbon-gray-50 mt-0.5">
              {styleColorOf(ticket) || '—'} · {poLoading ? '…' : po?.customer || '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="text-carbon-gray-50 hover:text-carbon-ink p-1 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body (gray-10) */}
        <div className="flex-1 overflow-y-auto px-6 py-[22px] flex flex-col gap-[18px]">
          <div className="grid grid-cols-2 gap-2.5">
            {stats.map((s) => (
              <div key={s.label} className="bg-carbon-white border border-carbon-border px-3.5 py-2.5">
                <div className="font-plex-mono text-[9.5px] tracking-[0.1em] uppercase text-carbon-gray-50 mb-0.5">
                  {s.label}
                </div>
                <div
                  className="font-plex-mono text-[17px] font-semibold"
                  style={{ color: s.accent ? row.sev : '#161616' }}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>

          {/* Agent's read */}
          <div
            className="border px-[18px] py-4"
            style={{ background: agent.tint, borderColor: READ_BORDERS[agent.key] || agent.color }}
          >
            <div className="flex items-center gap-[7px] mb-2">
              <Sparkles size={14} style={{ color: agent.color }} />
              <span
                className="font-plex-mono text-[10.5px] tracking-[0.14em] uppercase"
                style={{ color: agent.color }}
              >
                {agent.name}&rsquo;s read
              </span>
            </div>
            <div className="text-[13.5px] leading-relaxed text-carbon-gray-80">{agentReadOf(ticket)}</div>
            <div className="flex items-center gap-1.5 mt-2.5 text-xs text-carbon-gray-50">
              <ArrowRight size={13} style={{ color: agent.color }} />
              <span className="font-semibold text-carbon-gray-70">Suggested:</span> {row.suggested}
            </div>
          </div>

          {ticket.state === 'snoozed' ? (
            <div className="flex items-center gap-2 text-[13px] text-carbon-gray-70">
              <Clock size={14} />
              Snoozed
              {ticket.snoozed_until
                ? ` until ${toDateSafe(ticket.snoozed_until)?.toLocaleDateString('en-US') || '—'}`
                : ' (permanent)'}
              .
            </div>
          ) : null}
        </div>

        {/* Footer (white) */}
        <div className="bg-carbon-white border-t border-carbon-border px-6 py-4">
          {showReason ? (
            <div>
              <label className="block text-[13px] font-semibold text-carbon-gray-80 mb-1.5">
                Why is this OK?
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  autoFocus
                  placeholder="e.g. Customer extended cancel date; shipping Friday."
                  className="mt-1.5 w-full box-border border border-carbon-gray-30 bg-carbon-gray-10 px-3 py-2 text-[13.5px] font-normal font-plex resize-y focus:outline-none focus:border-carbon-blue-60"
                />
              </label>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowReason(false);
                    setReason('');
                  }}
                  disabled={busy}
                  className="px-3.5 py-2 text-[13px] text-carbon-gray-70 hover:text-carbon-ink disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => validReason && onMarkOk(reason.trim())}
                  disabled={!validReason || busy}
                  className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50"
                >
                  <Check size={14} /> Close ticket
                </button>
              </div>
              <div className="text-[11px] text-carbon-gray-50 mt-2">
                Your reason is saved to the PO record — {agent.name} learns from it.
              </div>
            </div>
          ) : (
            <div className="flex gap-2.5">
              <div className="relative flex-1">
                <button
                  type="button"
                  onClick={() => setSnoozeOpen((o) => !o)}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 bg-carbon-blue-60 text-carbon-white px-4 py-[11px] text-[13.5px] font-semibold hover:bg-carbon-blue-70 disabled:opacity-50"
                >
                  <Clock size={15} /> Snooze <ChevronDown size={13} />
                </button>
                {snoozeOpen ? (
                  <div className="absolute bottom-full mb-1.5 left-0 right-0 bg-carbon-white border border-carbon-border shadow-lg p-1 z-10">
                    {SNOOZE_OPTIONS.map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setSnoozeOpen(false);
                          onSnooze(snoozeUntil(key));
                        }}
                        className="w-full text-left px-3 py-2 text-[13px] text-carbon-gray-80 hover:bg-carbon-gray-10"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setShowReason(true)}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 bg-carbon-white border border-carbon-gray-30 text-carbon-gray-80 px-4 py-[11px] text-[13.5px] font-semibold hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                <Check size={15} /> Mark OK…
              </button>
            </div>
          )}
        </div>
    </>
  );
}
